import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Send, CheckCircle, ChevronDown, Mail, Plus, Minus, Loader2, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import PageHeader from '../components/PageHeader';
import { useSeoSettings, getCanonicalUrl } from '../contexts/SeoContext';

// ── ALL CONSTANTS PRESERVED ──
const ROLES = ['Writer','Director','Producer','Actor','Cinematographer','Editor','Composer','Other'];
const SUBMISSION_TYPES = ['Script','Concept','Proof-of-Concept','Collaboration','Brand/IP Partnership'];
const FORMATS = ['Short','Feature','Series','Documentary','Other'];
const GENRES = ['Horror','Thriller','Psychological','Supernatural','Slasher','Drama','Action','Sci-Fi','Dark Comedy'];
const PROJECT_STAGES = ['Idea','First Draft','Polished Draft','Proof-of-Concept','In Development'];
const ENQUIRY_TOPICS = ['General Question','Investment Opportunities','Distribution','Press/Media','Partnership','Other'];

const FAQ_ITEMS = [
  { question:"What kind of projects are you looking for?", answer:"We develop and produce bold, genre-driven stories with teeth. Horror, thriller, psychological, slasher, supernatural, crime, true-crime and war-leaning drama are in our wheelhouse. If it's safe, predictable, or trying to please everyone, it's probably not us." },
  { question:"Do you accept unsolicited scripts or attachments?", answer:"You can upload one supporting document (script, pitch deck, or similar) with your submission — PDF, DOC, or PPT up to 25 MB. If there's alignment, we may request additional materials securely." },
  { question:"What should I submit first?", answer:"Start with a strong logline and a link to your pitch materials (deck, lookbook, or teaser). Keep it lean. If it hooks us, we'll ask for the next layer." },
  { question:"What formats do you work with?", answer:"Short films, feature films, series, and documentaries (selectively). Choose the format that best serves the story, not the one that feels easiest to \"get made.\"" },
  { question:"I'm cast/crew. How do I get into your database?", answer:"Join the Pack is our cast & crew hub for indie filmmakers. Create your free profile and be part of the network Shadow Wolves Productions turns to for talent discovery.", link:{ text:"Join the Pack", url:"https://unified-studio-core.emergent.host/join/v9PkQqU2dgss" } },
  { question:"Do you work with investors and partners?", answer:"Yes, through our Studio Access Portal. If you're interested in investment, you can", link:{ text:"REQUEST ACCESS", url:"/request-access" } },
  { question:"How long does it take to hear back?", answer:"If it's a fit, you'll hear from us. If it's not, you probably won't. We keep our focus tight so we can actually build." },
  { question:"Can I submit multiple projects?", answer:"Submit your best one first. If it connects, we'll open the door to more." },
  { question:"Aside from films, what else do you do?", answer:"We build more than projects. We're developing a studio ecosystem — tools, resources, and platforms designed to support independent creators." },
  { question:"Is Shadow Wolves just horror?", answer:"No. Genre is our backbone, but not our limit. We prioritise bold, commercially viable stories across film and series." },
];

const generateFAQSchema = () => ({
  "@context":"https://schema.org","@type":"FAQPage",
  mainEntity: FAQ_ITEMS.map(i => ({ "@type":"Question", name:i.question, acceptedAnswer:{"@type":"Answer", text:i.answer+(i.link?` ${i.link.text}: ${i.link.url}`:'')} }))
});

// ── STYLE TOKENS ──
const T = {
  glass: { background:'rgba(17,19,24,0.68)', backdropFilter:'blur(20px)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px' },
  mono: { fontFamily:'var(--font-mono)', fontSize:'9px', fontWeight:300, letterSpacing:'0.14em', textTransform:'uppercase' },
  label: { fontFamily:'var(--font-mono)', fontSize:'9px', fontWeight:300, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(238,240,242,0.4)', display:'block', marginBottom:'8px' },
  input: { background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 14px', fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:300, color:'var(--swp-white)', outline:'none', width:'100%', transition:'border-color 0.2s, background 0.2s' },
  inputErr: { borderColor:'rgba(200,80,80,0.5)' },
  errMsg: { fontFamily:'var(--font-mono)', fontSize:'8px', color:'rgba(200,100,100,0.8)', letterSpacing:'0.1em', marginTop:'5px' },
  ice: '#6a9dbe',
};

// ── 100 WOLVES CARD ──
const WolvesCard = () => (
  <div style={{ ...T.glass, padding:'20px 30px', borderColor:'rgba(107,26,26,0.25)' }} data-testid="wolves-section">
    <div style={{ ...T.mono, color:'rgba(139,32,32,0.7)', marginBottom:'8px' }}>Exclusive Investment</div>
    <h4 style={{ fontFamily:'var(--font-display)', fontSize:'24px', color:'var(--swp-white)', letterSpacing:'0.06em', marginBottom:'8px' }}>100 WOLVES</h4>
    <p style={{ fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:300, color:'rgba(238,240,242,0.42)', lineHeight:1.65, marginBottom:'14px' }}>
      A private investment community for bold independent cinema. One hundred wolves building iconic films together.
    </p>
    <Link to="/100-wolves"
      data-testid="wolves-cta-btn"
      className="btn-swp" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', padding:'9px 16px', fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.14em', textTransform:'uppercase', textDecoration:'none', color:'var(--swp-white)', background:'linear-gradient(135deg, rgba(107,26,26,0.6), rgba(139,32,32,0.6))', border:'0.5px solid rgba(107,26,26,0.4)', borderRadius:'999px', transition:'all 0.2s' }}>
      Learn More →
    </Link>
  </div>
);

// ── SPOT'D CARD ──
const JoinThePackCard = () => (
  <div style={{ ...T.glass, padding:'20px 30px' }} data-testid="join-pack-section">
    <div style={{ ...T.mono, color:'rgba(106,157,190,0.6)', marginBottom:'6px' }}>Get discovered</div>
    <h4 style={{ fontFamily:'var(--font-display)', fontSize:'20px', color:'var(--swp-white)', letterSpacing:'0.04em', marginBottom:'10px' }}>Join the Pack</h4>
    <p style={{ fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:300, color:'rgba(238,240,242,0.42)', lineHeight:1.65, marginBottom:'14px' }}>
      Our cast & crew hub for indie filmmakers. Create your free profile and be part of the network Shadow Wolves Productions turns to for talent discovery.
    </p>
    <button onClick={() => window.open('https://unified-studio-core.emergent.host/join/v9PkQqU2dgss','_blank')}
      data-testid="join-pack-btn"
      className="btn-swp btn-swp-primary" style={{ width:'100%', justifyContent:'center' }}>
      Create Your Profile
    </button>
  </div>
);

// ── WORD COUNTER ──
const WordCount = ({ text, suggested, max, label }) => {
  const count = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  const over = max && count > max;
  return (
    <span style={{ ...T.mono, fontSize:'8px', color: over ? 'rgba(200,100,100,0.8)' : 'rgba(238,240,242,0.2)', textTransform:'none' }}>
      ({count}{suggested ? ` / ~${suggested}` : ''} words{max ? `, max ${max}` : ''})
    </span>
  );
};

// ── SUBMIT PROJECT FORM ──
const SubmitProjectForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({ name:'',email:'',role:'',submission_type:'',format:'',genres:[],project_stage:'',logline:'',external_link:'',message:'',project_title:'',synopsis:'',website_url:'' });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOriginal, setConfirmOriginal] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false);
  const [confirmAiNotice, setConfirmAiNotice] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);
  const fileInputRef = useRef(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const ALLOWED_FILE_TYPES = ['.pdf','.doc','.docx','.pptx','.ppt','.txt'];
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(ext)) {
      toast.error('Accepted file types: PDF, DOC, DOCX, PPT, PPTX, TXT');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File must be under 25MB');
      return;
    }

    setAttachedFile(file);
    setUploadingFile(true);
    
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/submission-file`, { method:'POST', body: fd });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setUploadedFileInfo(data);
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
      setAttachedFile(null);
      setUploadedFileInfo(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const removeFile = () => {
    setAttachedFile(null);
    setUploadedFileInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const countWords = (t) => t ? t.trim().split(/\s+/).filter(Boolean).length : 0;

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name='Name is required';
    if (!formData.email.trim()) { e.email='Email is required'; } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email='Invalid email format';
    if (!formData.role) e.role='Please select your role';
    if (!formData.submission_type) e.submission_type='Please select submission type';
    if (!formData.format) e.format='Please select format';
    if (formData.genres.length===0) e.genres='Select at least one genre';
    if (!formData.project_stage) e.project_stage='Please select project stage';
    if (!formData.project_title.trim()) e.project_title='Project title is required';
    if (!formData.logline.trim()) { e.logline='Logline is required'; } else if (countWords(formData.logline) > 100) e.logline='Logline must be under 100 words';
    if (!formData.synopsis.trim()) { e.synopsis='Synopsis is required'; } else if (countWords(formData.synopsis) > 800) e.synopsis='Synopsis must be under 800 words';
    if (formData.message && countWords(formData.message) > 500) e.message='Message must be under 500 words';
    setErrors(e); return Object.keys(e).length===0;
  };
  const handleChange = (e) => { const {name,value}=e.target; setFormData(p=>({...p,[name]:value})); if(errors[name]) setErrors(p=>({...p,[name]:null})); };
  const handleGenreToggle = (g) => { setFormData(p=>({...p,genres:p.genres.includes(g)?p.genres.filter(x=>x!==g):[...p.genres,g]})); if(errors.genres) setErrors(p=>({...p,genres:null})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error('Please fix the errors below'); return; }
    if (!confirmOriginal||!confirmReview||!confirmAiNotice) { toast.error('Please confirm all required acknowledgements'); return; }
    if (uploadingFile) { toast.error('Please wait for file upload to complete'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        submission_type_tag:'project',
        confirm_original: confirmOriginal,
        confirm_review: confirmReview,
        confirm_ai_notice: confirmAiNotice,
        idempotency_key: idempotencyKey,
      };
      if (uploadedFileInfo) {
        payload.attachment_file_id = uploadedFileInfo.file_id;
        payload.attachment_filename = uploadedFileInfo.original_filename;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`,{ method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) });
      if (res.ok) { onSuccess(); }
      else { const err=await res.json(); toast.error(err.detail||'Submission failed'); }
    } catch { toast.error('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const SWPSelect = ({ name, value, onChange, options, placeholder, testId }) => (
    <div style={{ position:'relative' }}>
      <select name={name} value={value} onChange={onChange} data-testid={testId}
        style={{ ...T.input, ...(errors[name]?T.inputErr:{}), appearance:'none', cursor:'pointer', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(238,240,242,0.3)' stroke-width='1.2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 14px center', paddingRight:'40px', colorScheme:'dark' }}>
        <option value="" style={{ background:'#0d0f14', color:'rgba(238,240,242,0.4)' }}>{placeholder}</option>
        {options.map(o=><option key={o} value={o} style={{ background:'#0d0f14', color:'var(--swp-white)' }}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} data-testid="submit-project-form" style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
      {/* Honeypot — hidden from users */}
      <div style={{ position:'absolute', left:'-9999px', opacity:0, height:0, overflow:'hidden' }} aria-hidden="true">
        <input type="text" name="website_url" value={formData.website_url} onChange={handleChange} tabIndex={-1} autoComplete="off" />
      </div>
      {/* Project Title */}
      <div>
        <label style={T.label}>Project Title *</label>
        <input type="text" name="project_title" value={formData.project_title} onChange={handleChange} style={{ ...T.input,...(errors.project_title?T.inputErr:{}) }} placeholder="Working title of your project" data-testid="project-title-input" />
        {errors.project_title&&<p style={T.errMsg}>{errors.project_title}</p>}
      </div>
      {/* Name + Email */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <div><label style={T.label}>Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} style={{ ...T.input,...(errors.name?T.inputErr:{}) }} placeholder="Your name" data-testid="project-name-input" />{errors.name&&<p style={T.errMsg}>{errors.name}</p>}</div>
        <div><label style={T.label}>Email *</label><input type="email" name="email" value={formData.email} onChange={handleChange} style={{ ...T.input,...(errors.email?T.inputErr:{}) }} placeholder="your@email.com" data-testid="project-email-input" />{errors.email&&<p style={T.errMsg}>{errors.email}</p>}</div>
      </div>
      {/* Role + Type */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <div><label style={T.label}>Your Role *</label><SWPSelect name="role" value={formData.role} onChange={handleChange} options={ROLES} placeholder="Select role" testId="project-role-select" />{errors.role&&<p style={T.errMsg}>{errors.role}</p>}</div>
        <div><label style={T.label}>Submission Type *</label><SWPSelect name="submission_type" value={formData.submission_type} onChange={handleChange} options={SUBMISSION_TYPES} placeholder="Select type" testId="project-type-select" />{errors.submission_type&&<p style={T.errMsg}>{errors.submission_type}</p>}</div>
      </div>
      {/* Format + Stage */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <div><label style={T.label}>Format *</label><SWPSelect name="format" value={formData.format} onChange={handleChange} options={FORMATS} placeholder="Select format" testId="project-format-select" />{errors.format&&<p style={T.errMsg}>{errors.format}</p>}</div>
        <div><label style={T.label}>Project Stage *</label><SWPSelect name="project_stage" value={formData.project_stage} onChange={handleChange} options={PROJECT_STAGES} placeholder="Where is it at?" testId="project-stage-select" />{errors.project_stage&&<p style={T.errMsg}>{errors.project_stage}</p>}</div>
      </div>
      {/* Genres */}
      <div>
        <label style={T.label}>Genre(s) *</label>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
          {GENRES.map(g=>(
            <button key={g} type="button" onClick={()=>handleGenreToggle(g)} data-testid={`genre-chip-${g.toLowerCase()}`}
              style={{ ...T.mono, padding:'7px 14px', borderRadius:'999px', border:`0.5px solid ${formData.genres.includes(g)?T.ice:'rgba(255,255,255,0.09)'}`, background:formData.genres.includes(g)?'rgba(106,157,190,0.12)':'transparent', color:formData.genres.includes(g)?T.ice:'rgba(238,240,242,0.38)', cursor:'pointer', transition:'all 0.2s' }}>
              {g}
            </button>
          ))}
        </div>
        {errors.genres&&<p style={T.errMsg}>{errors.genres}</p>}
      </div>
      {/* Logline */}
      <div>
        <label style={T.label}>Logline * <WordCount text={formData.logline} suggested={60} max={100} /></label>
        <textarea name="logline" value={formData.logline} onChange={handleChange} rows={2} style={{ ...T.input,...(errors.logline?T.inputErr:{}), resize:'none' }} placeholder="One sentence. Make it count." data-testid="project-logline-input" />
        {errors.logline&&<p style={T.errMsg}>{errors.logline}</p>}
      </div>
      {/* Synopsis */}
      <div>
        <label style={T.label}>Synopsis * <WordCount text={formData.synopsis} suggested={300} max={800} /></label>
        <p style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.25)', textTransform:'none', marginBottom:'8px', lineHeight:1.6 }}>
          Outline the protagonist, central conflict, major developments and ending. Include spoilers. For a series, describe the overall premise and ongoing story engine.
        </p>
        <textarea name="synopsis" value={formData.synopsis} onChange={handleChange} rows={8} style={{ ...T.input,...(errors.synopsis?T.inputErr:{}), resize:'vertical', minHeight:'120px' }} placeholder="150–500 words recommended" data-testid="project-synopsis-input" />
        {errors.synopsis&&<p style={T.errMsg}>{errors.synopsis}</p>}
      </div>
      {/* Link + Message */}
      <div>
        <label style={T.label}>Material Link <span style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.2)', textTransform:'none' }}>(Drive, Vimeo, website — ensure viewing permissions are enabled)</span></label>
        <input type="url" name="external_link" value={formData.external_link} onChange={handleChange} style={T.input} placeholder="https://…" data-testid="project-link-input" />
      </div>
      <div>
        <label style={T.label}>Message <WordCount text={formData.message} max={500} /> <span style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.2)', textTransform:'none' }}>(optional)</span></label>
        <textarea name="message" value={formData.message} onChange={handleChange} rows={3} style={{ ...T.input, resize:'none' }} placeholder="Anything else?" data-testid="project-message-input" />
        {errors.message&&<p style={T.errMsg}>{errors.message}</p>}
      </div>
      {/* File Upload */}
      <div>
        <label style={T.label}>Supporting Document <span style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.2)', textTransform:'none' }}>(optional — script, pitch deck, or 1 file)</span></label>
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.pptx,.ppt,.txt" onChange={handleFileSelect} style={{ display:'none' }} data-testid="project-file-input" />
        {!attachedFile ? (
          <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="project-file-upload-btn"
            style={{ ...T.input, display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', cursor:'pointer', border:'1px dashed rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.02)', padding:'18px 14px', textAlign:'center' }}>
            <Upload size={16} style={{ color:'rgba(106,157,190,0.6)' }} />
            <span style={{ ...T.mono, color:'rgba(238,240,242,0.35)' }}>Click to upload — PDF, DOC, DOCX, PPT, TXT (max 25MB)</span>
          </button>
        ) : (
          <div style={{ ...T.input, display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', background:'rgba(106,157,190,0.06)', borderColor:'rgba(106,157,190,0.25)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
              <FileText size={18} style={{ color:T.ice, flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <div style={{ fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--swp-white)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{attachedFile.name}</div>
                <div style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.3)' }}>
                  {uploadingFile ? 'Uploading…' : uploadedFileInfo ? `${(attachedFile.size / 1024).toFixed(0)} KB — Uploaded` : 'Error'}
                </div>
              </div>
            </div>
            <button type="button" onClick={removeFile} data-testid="project-file-remove-btn"
              style={{ background:'none', border:'none', cursor:'pointer', padding:'4px', flexShrink:0 }}>
              <X size={16} style={{ color:'rgba(238,240,242,0.4)' }} />
            </button>
          </div>
        )}
      </div>
      {/* Confirmations */}
      <div style={{ borderTop:'0.5px solid rgba(255,255,255,0.07)', paddingTop:'16px', display:'flex', flexDirection:'column', gap:'12px' }}>
        {[
          { state:confirmOriginal, set:setConfirmOriginal, label:"I confirm this project is original or I control the rights to submit it. *", testId:"confirm-original-checkbox" },
          { state:confirmReview, set:setConfirmReview, label:"I understand that submitted materials will be reviewed at Shadow Wolves' discretion. *", testId:"confirm-review-checkbox" },
          { state:confirmAiNotice, set:setConfirmAiNotice, label:"I understand that submitted pitch information may be processed by an external AI service to assist Shadow Wolves Productions' internal review. This does not imply a transfer of rights or an agreement to produce the project. *", testId:"confirm-ai-notice-checkbox" },
        ].map(({state,set,label,testId})=>(
          <label key={testId} style={{ display:'flex', alignItems:'flex-start', gap:'12px', cursor:'pointer' }}>
            <input type="checkbox" checked={state} onChange={e=>set(e.target.checked)} data-testid={testId} style={{ marginTop:'2px', accentColor:T.ice, width:'14px', height:'14px' }} />
            <span style={{ fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:300, color:'rgba(238,240,242,0.45)', lineHeight:1.6 }}>{label}</span>
          </label>
        ))}
      </div>
      <button type="submit" disabled={submitting} data-testid="submit-project-btn" className="btn-swp btn-swp-primary" style={{ justifyContent:'center', opacity:submitting?0.6:1 }}>
        {submitting ? 'Processing…' : <><Send size={14} /> Submit Project</>}
      </button>
    </form>
  );
};

// ── GENERAL ENQUIRY FORM ──
const GeneralEnquiryForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({ name:'',email:'',phone:'',topic:'',message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e={};
    if (!formData.name.trim()) e.name='Name is required';
    if (!formData.email.trim()) { e.email='Email is required'; } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email='Invalid email';
    if (!formData.topic) e.topic='Please select a topic';
    if (!formData.message.trim()) e.message='Message is required';
    setErrors(e); return Object.keys(e).length===0;
  };
  const handleChange = (e) => { const {name,value}=e.target; setFormData(p=>({...p,[name]:value})); if(errors[name]) setErrors(p=>({...p,[name]:null})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fix the errors below'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`,{ method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...formData,service:formData.topic,submission_type_tag:'enquiry'}) });
      if (res.ok) { onSuccess(); toast.success('Message sent successfully'); }
      else { const err=await res.json(); toast.error(err.detail||'Failed to send'); }
    } catch { toast.error('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="general-enquiry-form" style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <div><label style={T.label}>Full Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} style={{ ...T.input,...(errors.name?T.inputErr:{}) }} placeholder="John Doe" data-testid="enquiry-name-input" />{errors.name&&<p style={T.errMsg}>{errors.name}</p>}</div>
        <div><label style={T.label}>Email *</label><input type="email" name="email" value={formData.email} onChange={handleChange} style={{ ...T.input,...(errors.email?T.inputErr:{}) }} placeholder="john@example.com" data-testid="enquiry-email-input" />{errors.email&&<p style={T.errMsg}>{errors.email}</p>}</div>
      </div>
      <div><label style={T.label}>Phone <span style={{ ...T.mono, fontSize:'8px', color:'rgba(238,240,242,0.2)', textTransform:'none' }}>(optional)</span></label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={T.input} placeholder="+61 XXX XXX XXX" data-testid="enquiry-phone-input" /></div>
      <div>
        <label style={T.label}>What can we help with? *</label>
        <div style={{ position:'relative' }}>
          <select name="topic" value={formData.topic} onChange={handleChange} data-testid="enquiry-topic-select" style={{ ...T.input,...(errors.topic?T.inputErr:{}), appearance:'none', cursor:'pointer', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(238,240,242,0.3)' stroke-width='1.2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 14px center', paddingRight:'40px' }}>
            <option value="">Select a topic</option>
            {ENQUIRY_TOPICS.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {errors.topic&&<p style={T.errMsg}>{errors.topic}</p>}
      </div>
      <div><label style={T.label}>Message *</label><textarea name="message" value={formData.message} onChange={handleChange} rows={5} style={{ ...T.input,...(errors.message?T.inputErr:{}), resize:'none' }} placeholder="Tell us about your enquiry…" data-testid="enquiry-message-input" />{errors.message&&<p style={T.errMsg}>{errors.message}</p>}</div>
      <button type="submit" disabled={submitting} data-testid="submit-enquiry-btn" className="btn-swp btn-swp-primary" style={{ justifyContent:'center', opacity:submitting?0.6:1 }}>
        {submitting ? 'Sending…' : <><Mail size={14} /> Send Message</>}
      </button>
    </form>
  );
};

// ── NEWSLETTER ──
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!email) return; setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`,{ method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({email,source:'work_with_us_page'}) });
      if (res.ok) { setSubscribed(true); toast.success('Welcome to the pack!'); }
      else { const err=await res.json(); toast.error(err.detail||'Failed to subscribe'); }
    } catch { toast.error('Connection error.'); }
    finally { setSubmitting(false); }
  };

  return (
    <section style={{ padding:'0 52px 100px' }}>
      <div className="swp-newsletter-grid" style={{ background:'rgba(17,19,24,0.68)', backdropFilter:'blur(20px)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px', padding:'56px 64px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'60px', alignItems:'center' }}>
        <div>
          <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(106,157,190,0.65)', display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
            <span style={{ width:'24px', height:'0.5px', background:'rgba(106,157,190,0.4)', display:'block' }}/>Stay in the loop
          </div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,3vw,42px)', color:'var(--swp-white)', letterSpacing:'0.02em', marginBottom:'12px' }}>Join the Pack</h2>
          <p style={{ fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:300, color:'rgba(238,240,242,0.5)', lineHeight:1.7 }}>Inside access to casting calls, industry updates, and the tools we actually use.</p>
        </div>
        <div>
          {subscribed ? (
            <div data-testid="newsletter-success" style={{ display:'flex', alignItems:'center', gap:'12px', color:'rgba(120,200,140,0.85)' }}>
              <CheckCircle size={22} />
              <span style={{ fontFamily:'var(--font-mono)', fontSize:'10px', letterSpacing:'0.12em', textTransform:'uppercase' }}>You're in. Welcome to the pack.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="newsletter-form" style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" data-testid="newsletter-name-input" style={{ background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 14px', fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:300, color:'var(--swp-white)', outline:'none', width:'100%', transition:'border-color 0.2s' }} />
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Enter your email" required data-testid="newsletter-email-input" style={{ background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 14px', fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:300, color:'var(--swp-white)', outline:'none', width:'100%', transition:'border-color 0.2s' }} />
              <button type="submit" disabled={submitting} data-testid="newsletter-submit-btn" className="btn-swp btn-swp-primary" style={{ justifyContent:'center', opacity:submitting?0.6:1 }}>
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:'8px', color:'rgba(238,240,242,0.2)', letterSpacing:'0.1em' }}>No spam. Unsubscribe anytime.</span>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// ── FAQ ──
const FAQSection = () => {
  const [open, setOpen] = useState({});
  const toggle = i => setOpen(p=>({...p,[i]:!p[i]}));
  return (
    <section style={{ padding:'0 52px 80px' }} data-testid="faq-section">
      <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(106,157,190,0.65)', display:'flex', alignItems:'center', gap:'16px', marginBottom:'28px' }}>
        <span style={{ width:'24px', height:'0.5px', background:'rgba(106,157,190,0.4)', display:'block' }}/>Frequently asked
        <div style={{ flex:1, height:'0.5px', background:'rgba(255,255,255,0.07)' }}/>
      </div>
      <div className="swp-faq-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1px', background:'rgba(255,255,255,0.07)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px', overflow:'hidden' }}>
        {FAQ_ITEMS.map((item,i)=>(
          <div key={`faq-${item.question.slice(0,20)}`} data-testid={`faq-item-${i}`} style={{ background:'rgba(13,15,20,0.75)', cursor:'pointer' }} onClick={()=>toggle(i)}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', padding:'22px 26px' }}>
              <span style={{ fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:400, color:'rgba(238,240,242,0.65)', lineHeight:1.5 }}>{item.question}</span>
              {open[i] ? <Minus size={15} style={{ color:'rgba(106,157,190,0.7)', flexShrink:0, marginTop:'2px' }} /> : <Plus size={15} style={{ color:'rgba(238,240,242,0.3)', flexShrink:0, marginTop:'2px' }} />}
            </div>
            {open[i] && (
              <div style={{ padding:'0 26px 22px', borderTop:'0.5px solid rgba(255,255,255,0.06)' }}>
                <p style={{ fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:300, color:'rgba(238,240,242,0.42)', lineHeight:1.7, paddingTop:'14px' }}>
                  {item.answer}
                  {item.link && <> <a href={item.link.url} target={item.link.url.startsWith('http')?'_blank':undefined} rel={item.link.url.startsWith('http')?'noopener noreferrer':undefined} style={{ color:'rgba(106,157,190,0.8)', textDecoration:'none' }}>{item.link.text}</a></>}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// ── SUCCESS STATE ──
const SuccessState = ({ type, onReset }) => (
  <div style={{ textAlign:'center', padding:'60px 0' }} data-testid={`${type}-success`}>
    <CheckCircle style={{ width:'52px', height:'52px', color:'rgba(120,200,140,0.85)', margin:'0 auto 20px' }} />
    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'28px', color:'var(--swp-white)', letterSpacing:'0.03em', marginBottom:'12px' }}>
      {type==='project' ? 'Submission received.' : 'Message sent.'}
    </h3>
    <p style={{ fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:300, color:'rgba(238,240,242,0.45)', marginBottom:'24px' }}>
      {type==='project' ? "Thank you for sharing your project with Shadow Wolves Productions. We'll contact you if we'd like to discuss it further." : "We'll get back to you as soon as possible."}
    </p>
    <button onClick={onReset} data-testid="submit-another-btn"
      style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(106,157,190,0.7)', background:'none', border:'none', cursor:'pointer' }}>
      Submit another {type==='project'?'project':'enquiry'}
    </button>
  </div>
);

// ── MAIN ──
const WorkWithUs = () => {
  const seoSettings = useSeoSettings();
  const [activeLane, setActiveLane] = useState(null);
  const [projectSubmitted, setProjectSubmitted] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const parallaxRef = useRef(null);
  useEffect(() => { window.scrollTo(0,0); }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) parallaxRef.current.style.transform = `translateY(${window.scrollY * 0.12}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const shouldRenderFaqSchema = seoSettings.organization_schema?.enable_faq_schema !== false;

  const LaneBtn = ({ lane, label, testId }) => {
    const active = activeLane===lane;
    return (
      <button onClick={() => setActiveLane(activeLane===lane?null:lane)} data-testid={testId}
        style={{ flex:1, padding:'12px 24px', fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.14em', textTransform:'uppercase', cursor:'pointer', borderRadius:'999px', transition:'all 0.25s', border:`0.5px solid ${active?'rgba(106,157,190,0.5)':'rgba(255,255,255,0.1)'}`, background:active?'rgba(106,157,190,0.1)':'rgba(17,19,24,0.6)', color:active?'var(--swp-ice)':'rgba(238,240,242,0.4)', backdropFilter:'blur(12px)' }}>
        {label}
      </button>
    );
  };

  return (
    <div className="work-with-us-page" style={{ paddingTop:'64px' }}>
      {/* Fixed parallax background */}
      <div ref={parallaxRef} style={{ position:'fixed', inset:0, zIndex:0, backgroundImage:'url("https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1800&q=80&fm=jpg")', backgroundSize:'cover', backgroundPosition:'center', backgroundRepeat:'no-repeat', filter:'brightness(0.22) saturate(0.45)', willChange:'transform' }} />
      <div style={{ position:'fixed', inset:0, zIndex:1, background:'rgba(5,6,8,0.55)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', inset:0, zIndex:2, background:'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 20%, rgba(8,9,11,0.75) 100%)', pointerEvents:'none' }} />

      <div style={{ position:'relative', zIndex:3 }}>
      <Helmet>
        <title>Work With Us | {seoSettings.global_seo?.site_name||'Shadow Wolves Productions'}</title>
        <meta name="description" content="Submit your project or get in touch with Shadow Wolves Productions." />
        <link rel="canonical" href={getCanonicalUrl('/work-with-us',seoSettings)} />
      </Helmet>
      {shouldRenderFaqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(generateFAQSchema()), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) }} />}

      <PageHeader page="workwithus" title="Work With Us" subtitle="Choose your lane. Submit with intent." />

      {/* ── FORM AREA ── */}
      <section style={{ padding:'48px 52px 80px' }}>
        <div className="swp-wwu-layout" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:'16px', alignItems:'start' }}>
          <div>
            {/* Lane buttons */}
            <div style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
              <LaneBtn lane="project" label="Submit a Project" testId="submit-project-toggle" />
              <LaneBtn lane="enquiry" label="General Enquiry" testId="general-enquiry-toggle" />
            </div>

            {/* Form panel */}
            {activeLane==='project' && (
              <div style={{ background:'rgba(17,19,24,0.68)', backdropFilter:'blur(20px)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px', overflow:'hidden' }} data-testid="project-lane">
                <div style={{ padding:'24px 32px 0' }}>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.18em', textTransform:'uppercase', color:'rgba(106,157,190,0.6)', marginBottom:'6px' }}>Submit a project</div>
                  <h2 style={{ fontFamily:'var(--font-display)', fontSize:'28px', color:'var(--swp-white)', letterSpacing:'0.03em', marginBottom:'20px' }}>Tell us about your project</h2>
                </div>
                <div style={{ padding:'0 32px 32px' }}>
                  {projectSubmitted ? <SuccessState type="project" onReset={()=>{ setProjectSubmitted(false); setActiveLane('project'); }} /> : <SubmitProjectForm onSuccess={()=>setProjectSubmitted(true)} />}
                </div>
              </div>
            )}

            {activeLane==='enquiry' && (
              <div style={{ background:'rgba(17,19,24,0.68)', backdropFilter:'blur(20px)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px', overflow:'hidden' }} data-testid="enquiry-lane">
                <div style={{ padding:'24px 32px 0' }}>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.18em', textTransform:'uppercase', color:'rgba(106,157,190,0.6)', marginBottom:'6px' }}>General enquiry</div>
                  <h2 style={{ fontFamily:'var(--font-display)', fontSize:'28px', color:'var(--swp-white)', letterSpacing:'0.03em', marginBottom:'20px' }}>Say something</h2>
                </div>
                <div style={{ padding:'0 32px 32px' }}>
                  {enquirySubmitted ? <SuccessState type="enquiry" onReset={()=>{ setEnquirySubmitted(false); setActiveLane('enquiry'); }} /> : <GeneralEnquiryForm onSuccess={()=>setEnquirySubmitted(true)} />}
                </div>
              </div>
            )}

            {!activeLane && (
              <div style={{ padding:'60px 0', textAlign:'center', color:'rgba(238,240,242,0.25)', fontFamily:'var(--font-mono)', fontSize:'10px', letterSpacing:'0.14em', textTransform:'uppercase' }}>
                Select an option above to get started.
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            <WolvesCard />
            <JoinThePackCard />
            <div style={{ background:'rgba(17,19,24,0.68)', backdropFilter:'blur(16px)', border:'0.5px solid rgba(255,255,255,0.07)', borderRadius:'14px', padding:'24px 26px' }}>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(238,240,242,0.3)', marginBottom:'10px' }}>Direct contact</div>
              <div style={{ fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:300, color:'rgba(238,240,242,0.45)', lineHeight:2 }}>
                <div>admin@shadowwolvesproductions.com.au</div>
                <div>+61 0420 984 558</div>
                <div style={{ color:'rgba(238,240,242,0.25)' }}>Sydney, NSW, Australia</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection />
      <NewsletterSection />
      </div>
    </div>
  );
};

export default WorkWithUs;
